import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Phone,
  Users,
  CheckSquare,
  Flag,
  Mail,
  Utensils,
  Clock,
  Calendar,
  MoreHorizontal,
  Link2,
  User,
  FileText,
  Layers,
} from "lucide-react";
import { toast } from "sonner";
import UserSelectByPermissions from "features/user-select-by-permission/user-select-by-permission";
import DatePicker from "features/datepicker/date-picker";
import ProjectActivityCard from "components/project/project-activity-card";
import ProjectActivityEditModal from "components/project/project-activity-edit-modal";
import {
  createProjectActivity,
  fetchProjectActivities,
  patchProjectActivity,
  deleteProjectActivity,
  ProjectActivity,
  ProjectActivityType,
  ProjectActivityPriority,
} from "services/project-activities.service";
import {
  combineLocal,
  toLocalDateParts,
  toYmd,
} from "components/project/project-activity-form-utils";

const ACTIVITY_TYPES: Array<{
  id: ProjectActivityType;
  labelKey: string;
  defaultLabel: string;
  Icon: typeof Phone;
}> = [
  { id: "CALL", labelKey: "project_activity_call", defaultLabel: "Call", Icon: Phone },
  {
    id: "MEETING",
    labelKey: "project_activity_meeting",
    defaultLabel: "Meeting",
    Icon: Users,
  },
  {
    id: "TASK",
    labelKey: "project_activity_task",
    defaultLabel: "Task",
    Icon: CheckSquare,
  },
  {
    id: "DEADLINE",
    labelKey: "project_activity_deadline",
    defaultLabel: "Deadline",
    Icon: Flag,
  },
  {
    id: "EMAIL",
    labelKey: "project_activity_email",
    defaultLabel: "Email",
    Icon: Mail,
  },
  {
    id: "LUNCH",
    labelKey: "project_activity_lunch",
    defaultLabel: "Lunch",
    Icon: Utensils,
  },
];

type FormState = {
  title: string;
  activityType: ProjectActivityType;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  priority: "" | ProjectActivityPriority;
  note: string;
  location: string;
  videoCallUrl: string;
  showExtras: boolean;
  availability: "BUSY" | "FREE";
  assignedTo: string | null;
  isDone: boolean;
};

function defaultForm(defaultAssigneeId?: string | null): FormState {
  const now = new Date();
  const end = new Date(now.getTime() + 30 * 60 * 1000);
  const startParts = toLocalDateParts(now);
  const endParts = toLocalDateParts(end);
  return {
    title: "Call",
    activityType: "CALL",
    startDate: startParts.date,
    startTime: startParts.time,
    endDate: endParts.date,
    endTime: endParts.time,
    priority: "",
    note: "",
    location: "",
    videoCallUrl: "",
    showExtras: false,
    availability: "BUSY",
    assignedTo: defaultAssigneeId || null,
    isDone: false,
  };
}

type Props = {
  companyId: string;
  projectId: string;
  /** Display name of this board card (deal). */
  cardName?: string;
  defaultAssigneeId?: string | null;
  /** Hide scheduled list — used when History panel shows activities. */
  composerOnly?: boolean;
  onSaved?: () => void;
};

export default function ProjectActivityPanel({
  companyId,
  projectId,
  cardName,
  defaultAssigneeId,
  composerOnly = false,
  onSaved,
}: Props) {
  const { t } = useTranslation();
  const [items, setItems] = useState<ProjectActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<ProjectActivity | null>(null);
  const [form, setForm] = useState<FormState>(() =>
    defaultForm(defaultAssigneeId)
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await fetchProjectActivities(companyId, projectId);
      setItems(Array.isArray(rows) ? rows : []);
    } catch {
      toast.error(
        t("project_activity_load_failed", {
          defaultValue: "Could not load activities.",
        })
      );
    } finally {
      setLoading(false);
    }
  }, [companyId, projectId, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setForm((prev) =>
      prev.assignedTo
        ? prev
        : { ...prev, assignedTo: defaultAssigneeId || null }
    );
  }, [defaultAssigneeId]);

  const typeLabel = useMemo(() => {
    const found = ACTIVITY_TYPES.find((x) => x.id === form.activityType);
    return found
      ? t(found.labelKey, { defaultValue: found.defaultLabel })
      : form.activityType;
  }, [form.activityType, t]);

  const setType = (type: ProjectActivityType) => {
    const found = ACTIVITY_TYPES.find((x) => x.id === type);
    const label = found
      ? t(found.labelKey, { defaultValue: found.defaultLabel })
      : type;
    setForm((prev) => ({
      ...prev,
      activityType: type,
      // Keep title in sync when it still matches a type label / empty.
      title:
        !prev.title ||
        ACTIVITY_TYPES.some(
          (x) =>
            prev.title === x.defaultLabel ||
            prev.title === t(x.labelKey, { defaultValue: x.defaultLabel })
        )
          ? label
          : prev.title,
    }));
  };

  const resetForm = () => setForm(defaultForm(defaultAssigneeId));

  const handleSave = async () => {
    const title = form.title.trim();
    if (!title) {
      toast.error(
        t("project_activity_title_required", {
          defaultValue: "Title is required.",
        })
      );
      return;
    }
    setSaving(true);
    try {
      await createProjectActivity(companyId, projectId, {
        title,
        activityType: form.activityType,
        startAt: combineLocal(form.startDate, form.startTime),
        endAt: combineLocal(form.endDate, form.endTime),
        priority: form.priority || null,
        note: form.note.trim() || null,
        location: form.location.trim() || null,
        videoCallUrl: form.videoCallUrl.trim() || null,
        availability: form.availability,
        assignedTo: form.assignedTo || null,
        isDone: form.isDone,
      });
      toast.success(
        t("project_activity_saved", { defaultValue: "Activity saved." })
      );
      resetForm();
      await load();
      onSaved?.();
    } catch {
      toast.error(
        t("project_activity_save_failed", {
          defaultValue: "Could not save activity.",
        })
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleDone = async (row: ProjectActivity) => {
    try {
      const updated = await patchProjectActivity(
        companyId,
        projectId,
        row.id,
        { isDone: !row.isDone }
      );
      setItems((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
      onSaved?.();
    } catch {
      toast.error(
        t("project_activity_save_failed", {
          defaultValue: "Could not save activity.",
        })
      );
    }
  };

  const remove = async (row: ProjectActivity) => {
    try {
      await deleteProjectActivity(companyId, projectId, row.id);
      setItems((prev) => prev.filter((x) => x.id !== row.id));
      onSaved?.();
    } catch {
      toast.error(
        t("project_activity_delete_failed", {
          defaultValue: "Could not delete activity.",
        })
      );
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Composer — Pipedrive-style */}
      <div className="rounded-md border border-[var(--p-color-border)] bg-[var(--p-color-bg-surface)]">
        <div className="border-b border-[var(--p-color-border)] px-4 py-3">
          <input
            type="text"
            value={form.title}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, title: e.target.value }))
            }
            placeholder={typeLabel}
            className="w-full border-0 bg-transparent text-lg font-semibold text-[var(--p-color-text)] outline-none placeholder:text-[var(--p-color-text-secondary)]"
          />
        </div>

        <div className="flex flex-wrap gap-1 border-b border-[var(--p-color-border)] px-3 py-2">
          {ACTIVITY_TYPES.map(({ id, labelKey, defaultLabel, Icon }) => {
            const selected = form.activityType === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setType(id)}
                className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-2.5 text-xs font-semibold max-md:min-h-11 lg:py-1.5 ${
                  selected
                    ? "bg-[var(--p-color-bg-fill-info)] text-[var(--p-color-text-info-on-bg-fill)]"
                    : "text-[var(--p-color-text-secondary)] hover:bg-[var(--p-color-bg-surface-secondary)]"
                }`}
              >
                <Icon size={14} strokeWidth={1.75} />
                {t(labelKey, { defaultValue: defaultLabel })}
              </button>
            );
          })}
        </div>

        <div className="flex flex-col gap-3 px-4 py-3">
          <div className="flex items-start gap-3">
            <Clock
              size={16}
              strokeWidth={1.75}
              className="mt-2.5 shrink-0 text-[var(--p-color-text-secondary)]"
            />
            <div className="flex min-w-0 flex-1 flex-wrap items-end gap-2">
              <div className="project-activity-datepicker min-w-[9.5rem]">
                <DatePicker
                  selected={form.startDate || null}
                  title={t("start_date", { defaultValue: "Start date" })}
                  fullWidth={false}
                  disableDatesAfter={false}
                  onChange={(d: Date | null) =>
                    setForm((prev) => ({ ...prev, startDate: toYmd(d) }))
                  }
                />
              </div>
              <input
                type="time"
                value={form.startTime}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, startTime: e.target.value }))
                }
                className="rounded border border-[var(--p-color-border)] bg-[var(--p-color-bg-surface)] px-2 py-1.5 text-base lg:text-sm"
              />
              <span className="mb-2 text-[var(--p-color-text-secondary)]">–</span>
              <input
                type="time"
                value={form.endTime}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, endTime: e.target.value }))
                }
                className="rounded border border-[var(--p-color-border)] bg-[var(--p-color-bg-surface)] px-2 py-1.5 text-base lg:text-sm"
              />
              <div className="project-activity-datepicker min-w-[9.5rem]">
                <DatePicker
                  selected={form.endDate || null}
                  title={t("end_date", { defaultValue: "End date" })}
                  fullWidth={false}
                  disableDatesAfter={false}
                  onChange={(d: Date | null) =>
                    setForm((prev) => ({ ...prev, endDate: toYmd(d) }))
                  }
                />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Calendar
              size={16}
              strokeWidth={1.75}
              className="shrink-0 text-[var(--p-color-text-secondary)]"
            />
            <select
              value={form.priority}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  priority: e.target.value as FormState["priority"],
                }))
              }
              className="min-w-[10rem] rounded border border-[var(--p-color-border)] bg-[var(--p-color-bg-surface)] px-2 py-1.5 text-base lg:text-sm"
            >
              <option value="">
                {t("project_activity_priority", { defaultValue: "Priority" })}
              </option>
              <option value="LOW">
                {t("project_activity_priority_low", { defaultValue: "Low" })}
              </option>
              <option value="MEDIUM">
                {t("project_activity_priority_medium", {
                  defaultValue: "Medium",
                })}
              </option>
              <option value="HIGH">
                {t("project_activity_priority_high", { defaultValue: "High" })}
              </option>
            </select>
          </div>

          <div className="flex items-start gap-3">
            <MoreHorizontal
              size={16}
              strokeWidth={1.75}
              className="mt-1 shrink-0 text-[var(--p-color-text-secondary)]"
            />
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() =>
                  setForm((prev) => ({
                    ...prev,
                    showExtras: !prev.showExtras,
                  }))
                }
                className="text-left text-sm font-medium text-[var(--p-color-text-link)]"
              >
                {t("project_activity_add_extras", {
                  defaultValue: "Add Location, Video call, Description",
                })}
              </button>
              {form.showExtras && (
                <div className="flex flex-col gap-2">
                  <input
                    type="text"
                    value={form.location}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        location: e.target.value,
                      }))
                    }
                    placeholder={t("project_activity_location", {
                      defaultValue: "Location",
                    })}
                    className="rounded border border-[var(--p-color-border)] px-2 py-1.5 text-base lg:text-sm"
                  />
                  <input
                    type="url"
                    value={form.videoCallUrl}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        videoCallUrl: e.target.value,
                      }))
                    }
                    placeholder={t("project_activity_video", {
                      defaultValue: "Video call URL",
                    })}
                    className="rounded border border-[var(--p-color-border)] px-2 py-1.5 text-base lg:text-sm"
                  />
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Layers
              size={16}
              strokeWidth={1.75}
              className="shrink-0 text-[var(--p-color-text-secondary)]"
            />
            <select
              value={form.availability}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  availability: e.target.value as "BUSY" | "FREE",
                }))
              }
              className="min-w-[10rem] rounded border border-[var(--p-color-border)] bg-[var(--p-color-bg-surface)] px-2 py-1.5 text-base lg:text-sm"
            >
              <option value="BUSY">
                {t("project_activity_busy", { defaultValue: "Busy" })}
              </option>
              <option value="FREE">
                {t("project_activity_free", { defaultValue: "Free" })}
              </option>
            </select>
          </div>

          <div className="flex items-start gap-3">
            <FileText
              size={16}
              strokeWidth={1.75}
              className="mt-2 shrink-0 text-[var(--p-color-text-secondary)]"
            />
            <textarea
              value={form.note}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, note: e.target.value }))
              }
              rows={4}
              placeholder={t("project_activity_note_placeholder", {
                defaultValue: "Notes",
              })}
              className="min-h-[96px] w-full resize-y rounded border border-[var(--p-color-border)] bg-[#fffbeb] px-3 py-2 text-base text-[var(--p-color-text)] outline-none lg:text-sm focus:border-[var(--p-color-border-focus)]"
            />
          </div>

          <div className="flex items-center gap-3">
            <User
              size={16}
              strokeWidth={1.75}
              className="shrink-0 text-[var(--p-color-text-secondary)]"
            />
            <div className="min-w-0 flex-1">
              <UserSelectByPermissions
                value={form.assignedTo || undefined}
                onChange={(val: string) =>
                  setForm((prev) => ({ ...prev, assignedTo: val || null }))
                }
                label="select_user"
                Permission="Access project"
              />
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Link2
              size={16}
              strokeWidth={1.75}
              className="mt-2 shrink-0 text-[var(--p-color-text-secondary)]"
            />
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <div className="rounded border border-[var(--p-color-border)] bg-[var(--p-color-bg-surface-secondary)] px-3 py-2 text-sm text-[var(--p-color-text)]">
                <span className="text-[var(--p-color-text-secondary)]">
                  {t("project_card_link", { defaultValue: "Card" })}:{" "}
                </span>
                {cardName ||
                  t("project_card", { defaultValue: "This card" })}
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 border-t border-[var(--p-color-border)] px-4 py-3">
          <label className="inline-flex items-center gap-2 text-sm text-[var(--p-color-text)]">
            <input
              type="checkbox"
              checked={form.isDone}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, isDone: e.target.checked }))
              }
              className="rounded border-[var(--p-color-border)]"
            />
            {t("project_activity_mark_done", { defaultValue: "Mark as done" })}
          </label>
          <div className="ml-auto flex items-center gap-2 max-md:ml-0 max-md:grid max-md:w-full max-md:grid-cols-2">
            <button
              type="button"
              onClick={resetForm}
              disabled={saving}
              className="rounded-md border border-[var(--p-color-border)] bg-[var(--p-color-bg-surface)] px-3 py-2 text-sm font-semibold text-[var(--p-color-text)] hover:bg-[var(--p-color-bg-surface-secondary)] max-md:min-h-11"
            >
              {t("cancel", { defaultValue: "Cancel" })}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="rounded-md bg-[var(--p-color-bg-fill-success)] px-4 py-2 text-sm font-semibold text-[var(--p-color-text-success-on-bg-fill)] hover:brightness-95 disabled:opacity-60 max-md:min-h-11"
            >
              {saving
                ? t("saving", { defaultValue: "Saving…" })
                : t("save", { defaultValue: "Save" })}
            </button>
          </div>
        </div>
      </div>

      <div
        className={
          composerOnly
            ? "mt-4 border-t border-[var(--p-color-border)] pt-4"
            : undefined
        }
      >
        <h3 className="mb-2 text-sm font-semibold text-[var(--p-color-text)]">
          {t("project_activity_scheduled", {
            defaultValue: composerOnly
              ? "Scheduled activities"
              : "Card activities",
          })}
        </h3>
        {loading ? (
          <p className="text-sm text-[var(--p-color-text-secondary)]">
            {t("loading", { defaultValue: "Loading…" })}
          </p>
        ) : items.length === 0 ? (
          <p className="text-sm text-[var(--p-color-text-secondary)]">
            {t("project_activity_empty", {
              defaultValue: "No activities yet.",
            })}
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {items.map((row) => (
              <li key={row.id}>
                <ProjectActivityCard
                  activity={row}
                  onToggleDone={() => toggleDone(row)}
                  onEdit={() => setEditing(row)}
                  onDelete={() => remove(row)}
                />
              </li>
            ))}
          </ul>
        )}
      </div>

      <ProjectActivityEditModal
        open={!!editing}
        companyId={companyId}
        projectId={projectId}
        activity={editing}
        onClose={() => setEditing(null)}
        onSaved={(updated) => {
          setItems((prev) =>
            prev.map((x) => (x.id === updated.id ? updated : x))
          );
          onSaved?.();
        }}
      />
    </div>
  );
}
